import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api";
import { useTranslation } from "../../hooks/useTranslation";
import usePostHog from "../../AppContent/posthog";

// Reusable, account-free newsletter signup block. `source` identifies where
// it was placed (website_footer, article, place_page, ...) so subscriber
// acquisition can be understood later without a schema change — see
// server/models/newsletterSubscriber.js.
//
// Styled for placement on the dark landing-page footer (its first home);
// a lighter-background placement would need its own color treatment.
const NewsletterSignup = ({ cities = [], source = "website_footer" }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();

  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consent) return; // submit button is already disabled without consent
    setError("");
    setSubmitting(true);
    try {
      await api.subscribeNewsletter({
        email,
        cities: city ? [city] : [],
        source,
        consent,
      });
      setSubmitted(true);
      // No email or other personal data goes to PostHog — city/source only.
      posthog.capture("newsletter_subscribe", { city: city || null, source });
    } catch (err) {
      setError(t.landingNewsletterError);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="card elev-sm" style={{ padding: 16 }}>
        <p style={{ margin: 0, fontSize: 14 }}>{t.landingNewsletterSuccess}</p>
      </div>
    );
  }

  const darkInputStyle = {
    background: "rgba(255,255,255,0.08)",
    borderColor: "rgba(255,255,255,0.25)",
    color: "var(--color-neutral-100)",
  };
  const darkLabelStyle = { color: "rgba(255,255,255,0.75)" };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <div className="field" style={{ flex: "2 1 180px", minWidth: 0 }}>
          <label htmlFor="newsletter-email" style={darkLabelStyle}>
            {t.landingNewsletterEmailLabel}
          </label>
          <input
            id="newsletter-email"
            className="input"
            style={darkInputStyle}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
        </div>
        {cities.length > 0 && (
          <div className="field" style={{ flex: "1 1 140px", minWidth: 0 }}>
            <label htmlFor="newsletter-city" style={darkLabelStyle}>
              {t.landingNewsletterCityLabel}
            </label>
            <select
              id="newsletter-city"
              className="input"
              style={darkInputStyle}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              disabled={submitting}
            >
              <option value="">{t.landingNewsletterCityAll}</option>
              {cities.map((c) => (
                <option key={c._id || c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          fontSize: 12,
          color: "rgba(255,255,255,0.75)",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          disabled={submitting}
          required
          style={{ marginTop: 2 }}
        />
        <span>
          {t.landingNewsletterConsent}{" "}
          <Link to="/privacy-policy" style={{ color: "inherit", textDecoration: "underline" }}>
            {t.landingFooterPrivacy}
          </Link>
        </span>
      </label>
      {error && <p style={{ fontSize: 13, color: "#e39a7a" }}>{error}</p>}
      <button type="submit" className="btn btn-primary" disabled={submitting || !consent}>
        {submitting ? t.landingNewsletterSubmitting : t.landingNewsletterSubmitBtn}
      </button>
    </form>
  );
};

export default NewsletterSignup;
